import type { PublicChef } from "@/lib/chefs.queries";
import { cityToCoords, regionToCoords } from "@/lib/chef-taxonomy";

export type MapChef = PublicChef & { lat: number; lng: number };

export function haversineKm(a: [number, number], b: [number, number]) {
  const R = 6371;
  const dLat = ((b[0] - a[0]) * Math.PI) / 180;
  const dLng = ((b[1] - a[1]) * Math.PI) / 180;
  const x =
    Math.sin(dLat / 2) ** 2 +
    Math.cos((a[0] * Math.PI) / 180) *
      Math.cos((b[0] * Math.PI) / 180) *
      Math.sin(dLng / 2) ** 2;
  return 2 * R * Math.asin(Math.sqrt(x));
}

/**
 * Deriva lat/lng per ogni chef.
 * - Prima la città (mappata sui capoluoghi italiani).
 * - Fallback sul centroide della regione, così uno chef con una città non mappata
 *   non scompare dalla mappa.
 * - Jitter deterministico (~150–600m) per evitare che chef nella stessa città
 *   stiano esattamente sullo stesso pixel: il primo marker ha posizione nominale,
 *   gli altri vengono distribuiti su un piccolo cerchio attorno.
 */
export function withCoords(chefs: PublicChef[]): MapChef[] {
  // Conta quanti chef condividono la stessa coordinata base, in ordine di lista.
  const seen = new Map<string, number>();
  const out: MapChef[] = [];
  for (const c of chefs) {
    const base = cityToCoords(c.city) ?? regionToCoords(c.region);
    if (!base) continue;
    const key = `${base[0].toFixed(4)},${base[1].toFixed(4)}`;
    const idx = seen.get(key) ?? 0;
    seen.set(key, idx + 1);
    let [lat, lng] = base;
    if (idx > 0) {
      // Spirale di Vogel a piccolo raggio: distribuisce uniformemente fino a ~50 chef
      // attorno allo stesso punto senza sovrapposizioni visibili sui marker.
      const golden = Math.PI * (3 - Math.sqrt(5));
      const r = 0.004 * Math.sqrt(idx); // ~440m per il primo offset, cresce con sqrt
      const theta = idx * golden;
      lat += r * Math.cos(theta);
      // Compensa la longitudine per la latitudine (cos lat) — su scala piccola.
      lng += (r * Math.sin(theta)) / Math.cos((lat * Math.PI) / 180);
    }
    out.push({ ...c, lat, lng });
  }
  return out;
}

export function sortByDistance(chefs: MapChef[], origin: [number, number]) {
  return [...chefs]
    .map((c) => ({ chef: c, km: haversineKm(origin, [c.lat, c.lng]) }))
    .sort((a, b) => a.km - b.km);
}